/**
 * Designtoken der Plattform — die **einzige** Quelle für Farbe, Abstand,
 * Schrift und Trefferfläche.
 *
 * ADR-040 macht daraus eine Auflage: Fremddienste im Portalrahmen bekommen ihr
 * Theme aus diesen Werten erzeugt, nicht aus einer zweiten, von Hand
 * gepflegten Farbliste. Zwei Listen laufen auseinander, und der Bruch ist dann
 * genau das, was der Rahmen verhindern sollte — der Benutzer sieht, dass er
 * das Werkzeug gewechselt hat.
 *
 * Deshalb steht hier nichts über Aussehen im Sinne von „schön". Jeder Wert
 * trägt eine Rolle, und Rollen lassen sich prüfen: `kontrast.ts` rechnet nach,
 * ob Vordergrund auf Hintergrund lesbar ist, und `theme.ts` erzeugt daraus
 * beides — die CSS-Variablen der Shell und das Theme für Fremddienste.
 *
 * `07` §7.14 gibt die Randbedingungen vor, und sie sind Feldanforderungen,
 * keine Geschmacksfragen: hoher Kontrast, Hell und Dunkel, große Touchziele,
 * **keine farbabhängige Statusinformation**. Der letzte Punkt ist der, den
 * Token allein nicht durchsetzen können — er gehört in die Bauteile: Ein
 * Zustand braucht Text oder Form, nicht nur Farbe.
 */

/** Farbrollen. Die Namen sagen, wofür — nicht, wie es aussieht. */
export interface Farbsatz {
  /** Grundfläche der Seite. */
  readonly flaeche: string;
  /** Erhobene Fläche: Karten, Dialoge, der Header. */
  readonly flaecheErhoben: string;
  /** Trennlinien und Umrandungen. */
  readonly linie: string;
  /** Fließtext auf `flaeche`. */
  readonly text: string;
  /** Nebentext: Zeitangaben, Herkunft, Hilfetexte. */
  readonly textLeise: string;
  /** Markenfarbe des Betriebs. Trägt Aktionen, nicht Zustände. */
  readonly akzent: string;
  /** Text auf `akzent`. */
  readonly aufAkzent: string;
  /** Erfolg, Warnung, Fehler — immer zusammen mit Text oder Form. */
  readonly erfolg: string;
  readonly warnung: string;
  readonly fehler: string;
  /** Fokusring. Muss auf jeder Fläche sichtbar sein. */
  readonly fokus: string;
}

/**
 * Hell.
 *
 * **Der Akzent ist ein Vorgabewert, keine Festlegung.** Die Markenfarbe des
 * Betriebs steht noch nicht fest; sie wird später je Kundenstack konfiguriert.
 * Bis dahin steht hier ein Blau, das die Kontrastprüfung besteht — wer es
 * ersetzt, lässt `pnpm test` entscheiden, ob der Ersatz das auch tut.
 */
export const HELL: Farbsatz = {
  // Dieselbe Familie wie die dunkle Fassung, nur andersherum: leicht kühle,
  // blaustichige Neutraltöne statt reinem Grau. Sonst wirkt der Wechsel
  // zwischen den Erscheinungen wie ein Wechsel des Programms.
  flaeche: "#ffffff",
  flaecheErhoben: "#f1f4f8",
  linie: "#b9c3d1",
  text: "#101725",
  textLeise: "#56637a",
  akzent: "#0b5cad",
  aufAkzent: "#ffffff",
  erfolg: "#136c34",
  warnung: "#8a5300",
  fehler: "#a8231b",
  fokus: "#0b5cad",
};

/**
 * Dunkel.
 *
 * Nicht die invertierte Fassung von `HELL`: Auf dunklem Grund brauchen
 * gesättigte Farben mehr Helligkeit, sonst verschwinden sie. Die Werte sind
 * einzeln gegen die Kontrastschwelle gesetzt, nicht gerechnet.
 */
export const DUNKEL: Farbsatz = {
  // Marineblau statt Neutralgrau — angelehnt an die Anmutung von Bulwark, dem
  // Webmailer, den das Portal nach ADR-040 rahmt. Zwei Oberflächen
  // nebeneinander, von denen eine grau und die andere blau ist, sehen nach
  // zwei Programmen aus; genau das soll der Rahmen vermeiden.
  //
  // **Übernommen ist die Anmutung, nicht die Marke.** Bulwarks Akzent ist ein
  // Himbeerrot (`#c12649`); es zu tragen hieße, das eigene Produkt in fremdem
  // Namen zu zeigen. Und keine Zeile Code: Bulwark steht unter AGPL-3.0, das
  // Portal nicht (`09` §9.2).
  flaeche: "#070b16",
  flaecheErhoben: "#0f1626",
  linie: "#334463",
  text: "#f2f5f9",
  textLeise: "#9aa6b8",
  akzent: "#7ab6f5",
  aufAkzent: "#08111f",
  erfolg: "#69d18d",
  warnung: "#e8b060",
  fehler: "#f2867e",
  fokus: "#7ab6f5",
};

/**
 * Trefferflächen und Abstände.
 *
 * `07` §7.14 verlangt große Touchziele und Bedienung mit einer Hand — die
 * Monteur-App wird auf der Baustelle bedient, teils mit Handschuhen. 44 CSS-
 * Pixel sind die untere Grenze, unter der Trefferquoten messbar einbrechen;
 * `beruehrungGross` ist der Wert für die Hauptaktionen eines Einsatzes.
 */
export const MASS = {
  beruehrung: "44px",
  beruehrungGross: "56px",
  /** Abstandsleiter. Vielfache von 4, damit nichts dazwischenrutscht. */
  abstand: {
    xs: "4px",
    s: "8px",
    m: "16px",
    l: "24px",
    xl: "40px",
  },
  radius: {
    s: "4px",
    m: "8px",
    gross: "16px",
  },
  /** Breite des Fokusrings. Sichtbar, nicht dezent — sonst ist er nutzlos. */
  fokusbreite: "3px",
} as const;

/**
 * Schrift.
 *
 * Systemschriften statt eigener Dateien: Sie sind sofort da, kosten keine
 * Ladezeit auf mobilem Netz und stellen keine Frage nach Herkunft und Lizenz.
 * `07` §7.14 verlangt kurze, eindeutige Texte — dafür braucht es keine eigene
 * Schrift, sondern ausreichende Größe.
 */
export const SCHRIFT = {
  familie:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  familieFest:
    'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", monospace',
  groesse: {
    klein: "13px",
    grund: "16px",
    gross: "20px",
    ueberschrift: "24px",
  },
  zeilenhoehe: "1.5",
} as const;

/** Die beiden Erscheinungen, die `07` §7.14 verlangt. */
export const ERSCHEINUNGEN = ["hell", "dunkel"] as const;
export type Erscheinung = (typeof ERSCHEINUNGEN)[number];

export function farbsatz(erscheinung: Erscheinung): Farbsatz {
  return erscheinung === "dunkel" ? DUNKEL : HELL;
}
